import React from "react";
import type { DepGraph, DepGraphPredType } from "../../../models";
import { type ExternalCandidate } from "../depCore/DepChipTypeahead/DepChipTypeahead";
import type { DepEditIntents } from "../depCore/DepTable/DepTable";
import SequenceView from "../SequenceView/SequenceView";
import "./DepEditorModal.css";

export interface Props {
  /** Header label, e.g. the Takt section name. */
  sectionTitle: string;
  /** The flat, already-cascaded scheduled graph from the host. The host owns
   *  fetching it (re-cascaded live with the staged edits) and recomputing it
   *  after each edit. */
  graph: DepGraph;
  /** The section's committed (pre-edit) dates per graph id — what "Board dates"
   *  mode shows. Frozen at open; "Forward pass" shows the live re-cascade. */
  boardDates?: Map<string, { start: string | null; finish: string | null }>;

  // --- edit intents (the host owns the staged edits + cascade) -------------
  /** Add an edge predId -> succId (default FS, 0 lag). */
  onAddEdge: (predId: string, succId: string) => void;
  /** Remove the edge between succId and predId. */
  onRemoveEdge: (succId: string, predId: string) => void;
  /** Change an edge's relationship type / lag (days). */
  onRetypeEdge: (
    succId: string,
    predId: string,
    type: DepGraphPredType,
    lagDays: number,
  ) => void;
  /** Stage a duration change (workdays) for an in-scope activity. Flows into the
   *  live forward-pass preview and commits with the edits on Save. */
  onEditDuration: (graphId: string, days: number) => void;
  /** Out-of-section search for the pred/succ "+ add" tie pickers. */
  fetchExternals: (query: string) => Promise<ExternalCandidate[]>;

  // --- lifecycle ----------------------------------------------------------
  onClose: () => void;
  /** Commit the staged edits. The host decides whether to close on save. */
  onSave: () => void;
  /** Disable the Save button while a host RPC is in flight. */
  saveDisabled?: boolean;
  /** When true, Save closes the modal automatically (default). A host that
   *  runs a confirm/cascade pass before committing sets this false. */
  closeOnSave?: boolean;

  loading: boolean;
  error: string | null;
}

const DepEditorModal: React.FC<Props> = ({
  sectionTitle,
  graph,
  boardDates,
  onAddEdge,
  onRemoveEdge,
  onRetypeEdge,
  onEditDuration,
  fetchExternals,
  onClose,
  onSave,
  saveDisabled = false,
  closeOnSave = true,
  loading,
  error,
}) => {
  // Board dates (the section's current schedule) ↔ Forward pass (live re-cascade
  // of the staged edits). Opens on the board so the editor reads as "what's
  // scheduled now"; flip to Forward pass to preview the recalculated dates that
  // Save commits.
  const [mode, setMode] = React.useState<"board" | "live">("board");

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Esc cancels an in-progress edit first: a focused field (duration cell,
      // typeahead search) or an open tie / work-code popover swallows it, so it
      // never tears down the whole editor and the staged edits with it.
      const ae = document.activeElement as HTMLElement | null;
      if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.isContentEditable)) {
        return;
      }
      if (document.querySelector(".dep-typeahead, .dep-chip-popover, .work-code-field-pop")) {
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const intents = React.useMemo<DepEditIntents>(
    () => ({ onAddEdge, onRemoveEdge, onRetypeEdge }),
    [onAddEdge, onRemoveEdge, onRetypeEdge],
  );

  const handleSave = React.useCallback(() => {
    onSave();
    if (closeOnSave) onClose();
  }, [onSave, closeOnSave, onClose]);

  return (
    <div
      className="dep-editor-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dep-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dep-editor-header">
          <div className="dep-editor-title">
            <span className="dep-editor-title-main">Edit Dependencies</span>
            <span className="dep-editor-section">{sectionTitle}</span>
          </div>
          <button className="dep-editor-close" onClick={onClose} title="Close (Esc)">
            ×
          </button>
        </div>
        <div className="dep-editor-body">
          <SequenceView
            variant="plan"
            mode={mode}
            onModeChange={setMode}
            modeNote="Forward pass recalculates dates from your ties"
            boardDates={boardDates}
            drivingOnlyInBar
            graph={graph}
            loading={loading}
            error={error}
            readOnly={false}
            intents={intents}
            fetchExternals={fetchExternals}
            onEditDuration={onEditDuration}
          />
        </div>
        <div className="dep-editor-footer">
          <button type="button" className="dep-editor-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="dep-editor-save"
            onClick={handleSave}
            disabled={saveDisabled}
          >
            {saveDisabled ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepEditorModal;
